import { listedOnTools } from './outreach-alfred';

/**
 * The connect hint under a Home tab's title: what connecting would unlock in that tab, then the tab's
 * apps that are not connected yet, each one starting its connect in place. Source: the product
 * prototype (`home-v3.src.html`, `SUGG` / `suggLine`) and `home-v3/HOME-TAB-CONNECTOR-SUGGESTIONS.md`.
 *
 * The apps per tab are a hand-picked product list, not the global popularity order the home rail uses.
 * Activity and Apps have no hint. When nothing in the list is left to offer, the tab shows its own
 * description instead.
 *
 * Import-light so it is unit-testable in the node-env suite. Ported 1:1 to iOS/Android
 * `HomeTabConnectors`; all three are pinned to `native-apps/shared/home-tab-connectors/hints.json`.
 */

export const HOME_TAB_CONNECTOR_HINTS = {
  tasks: { hintKey: 'categoryView.tasks.connectHint', toolkits: ['gmail', 'calendar-google'] },
  routines: { hintKey: 'categoryView.routines.connectHint', toolkits: ['calendar-google', 'gmail'] },
  goals: { hintKey: 'categoryView.goals.connectHint', toolkits: ['instagram', 'facebook'] },
  files: { hintKey: 'categoryView.files.connectHint', toolkits: ['googledrive'] },
  outreach: { hintKey: 'categoryView.outreach.connectHint', toolkits: ['whatsapp', 'gmail', 'instagram'] },
} as const satisfies Record<string, { hintKey: string; toolkits: readonly string[] }>;

export type HomeTabWithConnectHint = keyof typeof HOME_TAB_CONNECTOR_HINTS;

/** Any connected calendar counts for every calendar, so with one connected no calendar is offered. */
export const CALENDAR_TOOLKITS: readonly string[] = ['calendar-google', 'calendar-microsoft', 'calendar-apple'];

/**
 * The tab's apps to offer, in the tab's order. An app is dropped when it is connected, when it is a
 * calendar and any calendar is connected, or when the Tools tab has no row for it on this account (not in
 * the catalog, hidden, or connectors disabled) — there would be nothing to connect.
 */
export function homeTabConnectorHints<T extends { toolkit: string; connected: boolean; hiddenFromConnectorsList?: boolean }>(
  tab: HomeTabWithConnectHint,
  connectors: readonly T[],
  alfredOnOutreach: boolean,
): T[] {
  const hasCalendar = connectors.some((c) => c.connected && CALENDAR_TOOLKITS.includes(c.toolkit));
  const listed = connectors.filter((c) => listedOnTools(c, alfredOnOutreach));
  return HOME_TAB_CONNECTOR_HINTS[tab].toolkits.flatMap((toolkit) => {
    if (hasCalendar && CALENDAR_TOOLKITS.includes(toolkit)) return [];
    const row = listed.find((c) => c.toolkit === toolkit);
    return row && !row.connected ? [row] : [];
  });
}

/** The hint only shows on the home's tabs: the home's BI page (`HOME3_PAGE`, kept out of this import-light
 *  module; a test pins the two together). */
export const HOME_TAB_BI_PAGE = 'home_overview';

/** The BI button name of a hint link: the tab and the app, the same string on web, iOS and Android. */
export function homeTabConnectBiName(tab: HomeTabWithConnectHint, toolkit: string): string {
  return `home_tab_connect_${tab}_${toolkit}`;
}

/**
 * The hint's copy split around its `{apps}` slot, so the links can sit where each language puts them
 * ("…, connect {apps}" in English; other word orders put them elsewhere).
 */
export function splitConnectHint(copy: string): { before: string; after: string } {
  const at = copy.indexOf('{apps}');
  if (at === -1) return { before: copy, after: '' };
  return { before: copy.slice(0, at), after: copy.slice(at + '{apps}'.length) };
}
